import { Skeleton } from '@/components/ui/skeleton'
import { getInterviewSchedule } from '@/modules/Dashboard/services'
import { InterviewSchedule } from '@/modules/Dashboard/types'
import { generatePathDatacentral, paths } from '@/utils/path'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import 'dayjs/locale/vi'
import {
    Calendar,
    Mail,
    Phone,
    Briefcase,
    Clock,
    User,
    Building2,
    UserCheck,
    Users,
} from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button, Typogaphy } from 'vnetwork-ui'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

dayjs.extend(isoWeek)
dayjs.locale('vi')

const InterviewScheduleSkeleton = () => (
    <div className="space-y-3">
        {[1, 2, 3].map((i) => (
            <div key={i} className="p-3 border rounded-lg space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
                <Skeleton className="h-3 w-2/3" />
            </div>
        ))}
    </div>
)

interface InterviewCardProps {
    interview: InterviewSchedule
    onClick?: () => void
}

const InterviewCard: React.FC<InterviewCardProps> = ({
    interview,
    onClick,
}) => {
    const { t } = useTranslation('dashboard')
    const interviewTime = dayjs(interview.interview_time)
    const isToday = interviewTime.isSame(dayjs(), 'day')
    const isTomorrow = interviewTime.isSame(dayjs().add(1, 'day'), 'day')

    const getDayLabel = () => {
        if (isToday) return t('interview_schedule.today')
        if (isTomorrow) return t('interview_schedule.tomorrow')
        return interviewTime.format('dddd')
    }

    const getBadgeColor = () => {
        if (isToday) return 'bg-green-500 text-white'
        if (isTomorrow) return 'bg-yellow-500 text-white'
        return 'bg-primary text-white'
    }

    return (
        <div
            className="px-4 py-2 border rounded-md hover:shadow-md transition-all cursor-pointer bg-white"
            onClick={onClick}
        >
            {/* Header: Name + Day Badge */}
            <div className="flex items-start md:items-center justify-between mb-2 flex-col-reverse md:flex-row gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <User className="w-4 h-4 text-primary" />
                    <Typogaphy className="font-semibold text-sm text-gray-900 truncate capitalize">
                        {interview.name}
                    </Typogaphy>
                </div>
                <div
                    className={`flex items-center gap-1 px-4 py-1 rounded-full ${getBadgeColor()}`}
                >
                    <span className="text-[10px] font-medium leading-tight capitalize">
                        {getDayLabel()}
                        {','}
                    </span>
                    <span className="text-[10px] font-medium leading-tight">
                        {interviewTime.format('DD/MM')}
                    </span>
                </div>
            </div>

            {/* Position Info */}
            <div className="flex flex-wrap items-center gap-1.5 mb-2">
                {interview.job_title && (
                    <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-4 py-0.5 rounded-full">
                        <Briefcase className="w-3 h-3" />
                        {interview.job_title}
                    </span>
                )}
                {interview.department && (
                    <span className="inline-flex items-center gap-1 text-xs bg-purple-50 text-purple-700 px-4 py-0.5 rounded-full">
                        <Building2 className="w-3 h-3" />
                        {interview.department}
                    </span>
                )}
                {interview.type_of_position && (
                    <span className="inline-flex items-center gap-1 text-xs bg-orange-50 text-orange-700 px-4 py-0.5 rounded-full">
                        <UserCheck className="w-3 h-3" />
                        {interview.type_of_position}
                    </span>
                )}
            </div>

            {/* Time */}
            <div className="flex items-center gap-2 mb-2 p-2 bg-gray-100 rounded">
                <Clock className="w-4 h-4 text-primary" />
                <span className="text-xs font-medium text-gray-700 capitalize">
                    {interviewTime?.format('HH:mm')} -{' '}
                    {interviewTime?.format('dddd, DD/MM/YYYY')}
                </span>
            </div>

            {/* Interviewers */}
            {interview?.interviewers && interview?.interviewers?.length > 0 && (
                <div className="flex items-start gap-2 mb-2">
                    <Users className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex flex-wrap gap-1">
                        {interview.interviewers.map((interviewer) => (
                            <span
                                key={interviewer.id}
                                className="text-xs bg-primary/10 text-primary px-4 py-1 rounded-full font-medium capitalize"
                                title={interviewer.position}
                            >
                                {interviewer.label}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Contact Info */}
            <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
                {interview.email && (
                    <div className="flex items-center gap-1.5 text-gray-500">
                        <Mail className="w-3.5 h-3.5" />
                        <span className="text-xs truncate max-w-[140px]">
                            {interview.email}
                        </span>
                    </div>
                )}
                {interview.phone && (
                    <div className="flex items-center gap-1.5 text-gray-500">
                        <Phone className="w-3.5 h-3.5" />
                        <span className="text-xs">{interview.phone}</span>
                    </div>
                )}
            </div>
        </div>
    )
}

const InterviewScheduleWidget: React.FC = () => {
    const { t } = useTranslation('dashboard')
    const navigate = useNavigate()

    const { data, renderState } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.INTERVIEW_SCHEDULE],
            queryFn: () => getInterviewSchedule(),
            staleTime: 5 * 60 * 1000, // 5 minutes
        },
        {
            loadingComponent: <InterviewScheduleSkeleton />,
            errorDisplayMode: 'page',
        }
    )

    // Group interviews by this week and next week
    const { thisWeek, nextWeek } = useMemo(() => {
        const interviews = data || []
        const now = dayjs()
        const endOfThisWeek = now.endOf('isoWeek')
        const endOfNextWeek = now.add(1, 'week').endOf('isoWeek')

        const thisWeekInterviews: InterviewSchedule[] = []
        const nextWeekInterviews: InterviewSchedule[] = []

        interviews?.forEach((interview: InterviewSchedule) => {
            const interviewDate = dayjs(interview.interview_time)
            if (
                interviewDate.isBefore(endOfThisWeek) ||
                interviewDate.isSame(endOfThisWeek)
            ) {
                thisWeekInterviews.push(interview)
            } else if (
                interviewDate.isBefore(endOfNextWeek) ||
                interviewDate.isSame(endOfNextWeek)
            ) {
                nextWeekInterviews.push(interview)
            }
        })

        // Sort by interview time
        thisWeekInterviews.sort((a, b) =>
            dayjs(a.interview_time).diff(dayjs(b.interview_time))
        )
        nextWeekInterviews.sort((a, b) =>
            dayjs(a.interview_time).diff(dayjs(b.interview_time))
        )

        return { thisWeek: thisWeekInterviews, nextWeek: nextWeekInterviews }
    }, [data])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const handleViewAll = () => {
        navigate(generatePathDatacentral(paths.talentPoolManager, {}))
    }

    const handleInterviewClick = (interview: InterviewSchedule) => {
        navigate(
            generatePathDatacentral(paths.talentPoolDetail, {
                id: interview.id,
            })
        )
    }

    const hasInterviews = thisWeek.length > 0 || nextWeek.length > 0

    return (
        <div className="relative h-full">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-primary" />
                    <Typogaphy className="font-semibold text-primary">
                        {t('interview_schedule.title')}
                    </Typogaphy>
                </div>
                <span
                    className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full"
                    data-tour="interview-count"
                >
                    {thisWeek.length + nextWeek.length}{' '}
                    {t('interview_schedule.candidates')}
                </span>
            </div>

            <div className="h-[370px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                {hasInterviews ? (
                    <div className="space-y-4">
                        {/* This Week Section */}
                        {thisWeek.length > 0 && (
                            <div data-tour="interview-this-week">
                                <Typogaphy className="font-semibold text-primary text-sm mb-3">
                                    {t('interview_schedule.this_week')}
                                </Typogaphy>
                                <div className="space-y-2">
                                    {thisWeek.map((interview) => (
                                        <InterviewCard
                                            key={interview.id}
                                            interview={interview}
                                            onClick={() =>
                                                handleInterviewClick(interview)
                                            }
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Next Week Section */}
                        {nextWeek.length > 0 && (
                            <div data-tour="interview-next-week">
                                <Typogaphy className="font-semibold text-primary text-sm mb-3 pt-2">
                                    {t('interview_schedule.next_week')}
                                </Typogaphy>
                                <div className="space-y-2">
                                    {nextWeek.map((interview) => (
                                        <InterviewCard
                                            key={interview.id}
                                            interview={interview}
                                            onClick={() =>
                                                handleInterviewClick(interview)
                                            }
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <div className="w-12 h-12 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                            <Calendar className="w-6 h-6 text-gray-400" />
                        </div>
                        <Typogaphy className="text-gray-500 text-sm font-medium mb-2">
                            {t('interview_schedule.no_interviews')}
                        </Typogaphy>
                        <Typogaphy className="text-gray-400 text-xs">
                            {t('interview_schedule.no_interviews_description')}
                        </Typogaphy>
                    </div>
                )}
            </div>

            {/* View All Button */}
            {hasInterviews && (
                <div className="mt-4 pt-3 border-t border-gray-100 absolute bottom-0 left-0 right-0 bg-white">
                    <Button
                        size="small"
                        variant="text"
                        className="w-full !bg-bg-secondary-10 text-primary transition-colors"
                        onClick={handleViewAll}
                    >
                        {t('interview_schedule.view_all')}
                    </Button>
                </div>
            )}
        </div>
    )
}

export default InterviewScheduleWidget
